import type { Campaign } from '../../../../types';
import type {
  AlignedReviewerSyncSkip,
  AlignedReviewerSyncSummary,
} from '../../../../domain/users/repository';

type CampaignName = Pick<Campaign, 'id' | 'name'>;

function namesFor(skips: AlignedReviewerSyncSkip[], campaigns: CampaignName[]): string {
  return skips
    .map((skip) => campaigns.find((campaign) => campaign.id === skip.campaignId)?.name || skip.campaignId)
    .join(', ');
}

function sentenceFor(reason: string, skips: AlignedReviewerSyncSkip[], campaigns: CampaignName[]): string {
  const names = namesFor(skips, campaigns);
  const plural = skips.length > 1;
  switch (reason) {
    case 'user-not-linked':
      return 'Aligned was not updated because this user is not linked to an Aligned expert account.';
    case 'not-a-campaign-expert':
      return `Aligned was not updated for ${names}: this user is not an expert on ${plural ? 'those campaigns' : 'that campaign'} in Aligned.`;
    case 'campaign-not-aligned':
    case 'campaign-not-found':
      return `${names} ${plural ? 'are' : 'is'} not synced from Aligned, so there was nothing to update there.`;
    case 'emulator':
      return 'Aligned sync is skipped in the local emulator.';
    default:
      return `Aligned was not updated for ${names} (${reason}).`;
  }
}

/**
 * Turns the per-campaign Aligned mirror outcome of a role update into one
 * admin-facing sentence, or `null` when every change reached Aligned (or
 * there was nothing to mirror). Campaign ids are shown by name when the
 * campaign is loaded, falling back to the raw id.
 */
export function buildAlignedSyncNotice(
  summary: AlignedReviewerSyncSummary | undefined,
  campaigns: CampaignName[],
): string | null {
  const skipped = summary?.skipped ?? [];
  if (skipped.length === 0) return null;
  const byReason = new Map<string, AlignedReviewerSyncSkip[]>();
  for (const skip of skipped) {
    const group = byReason.get(skip.reason) ?? [];
    group.push(skip);
    byReason.set(skip.reason, group);
  }
  const sentences: string[] = [];
  byReason.forEach((skips, reason) => sentences.push(sentenceFor(reason, skips, campaigns)));
  return `Saved in Workbench. ${sentences.join(' ')}`;
}
